import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, FileRef, GroupVisibility, IsoDateTime, UserRef } from '@/shared/api/types';
import type { CommentItem } from '@/shared/components/comments';
import type { AudienceRequest } from '@/shared/components/pickers';
import type { ReactionSummary, ReactionType } from '@/shared/components/reactions';

/* ---------- Wire types (docs/modules/feed.md) ---------- */

export type PostType = 'message' | 'poll' | 'gratitude' | 'important' | 'activity';
export type FeedFilter = 'all' | 'mentions' | 'important' | 'mine' | 'favorites';
export type GratitudeBadge = 'thanks' | 'cup' | 'star' | 'heart' | 'rocket' | 'crown' | 'gift' | 'idea';

export const FEED_FILTERS: readonly FeedFilter[] = ['all', 'mentions', 'important', 'mine', 'favorites'];
export const GRATITUDE_BADGES: readonly GratitudeBadge[] = [
  'thanks',
  'cup',
  'star',
  'heart',
  'rocket',
  'crown',
  'gift',
  'idea',
];

export interface FeedGroupRef {
  id: string;
  name: string;
  avatarUrl: string | null;
  isProject: boolean;
  visibility: GroupVisibility;
}

export interface PostAudience {
  everyone: boolean;
  departmentIds: string[];
  groupIds: string[];
  groups: FeedGroupRef[];
  users: UserRef[];
}

export interface PollOption {
  id: string;
  text: string;
  votes: number;
  votedByMe: boolean;
}

export interface Poll {
  question: string;
  multiple: boolean;
  anonymous: boolean;
  closesAt: IsoDateTime | null;
  isClosed: boolean;
  totalVoters: number;
  options: PollOption[];
  hasVoted: boolean;
  canVote: boolean;
  canClose: boolean;
}

export interface Gratitude {
  badge: GratitudeBadge;
  recipients: UserRef[];
}

export interface Important {
  until: IsoDateTime | null;
  isActive: boolean;
  readCount: number;
  audienceCount: number;
  readByMe: boolean;
  recentReaders: UserRef[];
}

export interface Activity {
  module: string;
  type: string;
  entityId: string;
  title: string;
  link: string | null;
  params: Record<string, string>;
}

export interface FeedComment extends CommentItem {
  postId: string;
}

export interface Post {
  id: string;
  type: PostType;
  author: UserRef;
  title: string | null;
  bodyHtml: string;
  attachments: FileRef[];
  audience: PostAudience;
  createdAt: IsoDateTime;
  editedAt: IsoDateTime | null;
  pinnedAt: IsoDateTime | null;
  isFavorite: boolean;
  reactions: ReactionSummary[];
  recentReactors: UserRef[];
  commentCount: number;
  latestComments: CursorPage<FeedComment>;
  poll: Poll | null;
  gratitude: Gratitude | null;
  important: Important | null;
  activity: Activity | null;
  canEdit: boolean;
  canDelete: boolean;
  canPin: boolean;
}

export interface PollRequest {
  question: string;
  options: string[];
  multiple: boolean;
  anonymous: boolean;
  closesAt: IsoDateTime | null;
}

export interface PostRequest {
  type?: PostType;
  title: string | null;
  bodyHtml: string;
  attachmentIds: string[];
  audience?: AudienceRequest;
  poll?: PollRequest;
  gratitude?: { badge: GratitudeBadge; recipientIds: string[] };
  important?: { until: IsoDateTime | null };
}

export interface ReactionUser {
  user: UserRef;
  type: ReactionType;
  reactedAt: IsoDateTime;
}

export interface Reader {
  user: UserRef;
  readAt: IsoDateTime;
}

export interface Voter {
  user: UserRef;
  votedAt: IsoDateTime;
}

export interface FeedCounters {
  newPosts: number;
  unreadImportant: number;
}

export interface ImportantItem {
  id: string;
  author: UserRef;
  title: string | null;
  excerpt: string;
  createdAt: IsoDateTime;
  until: IsoDateTime | null;
}

export interface GroupFeedAccess {
  canRead: boolean;
  canPost: boolean;
  group: FeedGroupRef | null;
}

export interface StreamParams {
  filter: FeedFilter;
  groupId?: string | null;
  search?: string | null;
}

/** Birthdays and new employees (identity endpoints used by the feed widgets). */
export interface BirthdayItem {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  departmentName: string | null;
  isOnline: boolean;
  /** Day and month matter; the year is withheld from those who may not see it (the year 2000 stands in). */
  birthday: string;
  birthdayYearHidden?: boolean;
}

export interface NewEmployeeItem {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  departmentName: string | null;
  isOnline: boolean;
  joinedOn: string;
}

/* ---------- Keys ---------- */

export const feedKeys = {
  all: ['feed'] as const,
  streams: ['feed', 'stream'] as const,
  stream: (params: StreamParams) =>
    [
      'feed',
      'stream',
      { filter: params.filter, groupId: params.groupId ?? null, search: params.search ?? null },
    ] as const,
  pinnedAll: ['feed', 'pinned'] as const,
  pinned: (groupId?: string | null) => ['feed', 'pinned', groupId ?? null] as const,
  post: (id: string) => ['feed', 'post', id] as const,
  comments: (postId: string) => ['feed', 'comments', postId] as const,
  commentThread: (postId: string, mode: 'preview' | 'full') => ['feed', 'comments', postId, mode] as const,
  reactions: (postId: string, commentId?: string | null) =>
    ['feed', 'reactions', postId, commentId ?? null] as const,
  readers: (postId: string) => ['feed', 'readers', postId] as const,
  voters: (postId: string, optionId: string) => ['feed', 'voters', postId, optionId] as const,
  counters: ['feed', 'counters'] as const,
  importantUnread: ['feed', 'important-unread'] as const,
  groupAccess: (groupId: string) => ['feed', 'group-access', groupId] as const,
  birthdays: (month: number) => ['feed', 'widgets', 'birthdays', month] as const,
  newEmployees: ['feed', 'widgets', 'new-employees'] as const,
};

/* ---------- Requests ---------- */

const enc = encodeURIComponent;
export const STREAM_PAGE_SIZE = 20;
export const PREVIEW_COMMENTS = 3;
export const COMMENTS_PAGE_SIZE = 20;

export const feedApi = {
  stream: (params: StreamParams, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<Post>>('/feed/posts', {
      query: {
        filter: params.filter,
        groupId: params.groupId,
        search: params.search,
        cursor,
        limit: STREAM_PAGE_SIZE,
      },
      signal,
    }),
  pinned: (groupId: string | null | undefined, signal?: AbortSignal) =>
    api.get<Post[]>('/feed/posts/pinned', { query: { groupId }, signal }),
  post: (id: string, signal?: AbortSignal) => api.get<Post>(`/feed/posts/${enc(id)}`, { signal }),
  create: (request: PostRequest) => api.post<Post>('/feed/posts', request),
  update: (id: string, request: PostRequest) => api.put<Post>(`/feed/posts/${enc(id)}`, request),
  remove: (id: string) => api.delete(`/feed/posts/${enc(id)}`),
  pin: (id: string, pinned: boolean) =>
    pinned ? api.put<Post>(`/feed/posts/${enc(id)}/pin`) : api.delete<Post>(`/feed/posts/${enc(id)}/pin`),
  favorite: (id: string, favorite: boolean) =>
    favorite ? api.put(`/feed/posts/${enc(id)}/favorite`) : api.delete(`/feed/posts/${enc(id)}/favorite`),
  react: (id: string, type: ReactionType | null) =>
    type
      ? api.put<{ reactions: ReactionSummary[] }>(`/feed/posts/${enc(id)}/reaction`, { type })
      : api.delete<{ reactions: ReactionSummary[] }>(`/feed/posts/${enc(id)}/reaction`),
  reactions: (id: string, signal?: AbortSignal) =>
    api.get<ReactionUser[]>(`/feed/posts/${enc(id)}/reactions`, { signal }),
  read: (id: string) => api.post<{ readByMe: boolean; readCount: number }>(`/feed/posts/${enc(id)}/read`),
  readers: (id: string, signal?: AbortSignal) =>
    api.get<Reader[]>(`/feed/posts/${enc(id)}/readers`, { signal }),
  vote: (id: string, optionIds: string[]) =>
    api.post<Post>(`/feed/posts/${enc(id)}/poll/votes`, { optionIds }),
  retractVote: (id: string) => api.delete<Post>(`/feed/posts/${enc(id)}/poll/votes`),
  closePoll: (id: string) => api.post<Post>(`/feed/posts/${enc(id)}/poll/close`),
  voters: (id: string, optionId: string, signal?: AbortSignal) =>
    api.get<Voter[]>(`/feed/posts/${enc(id)}/poll/options/${enc(optionId)}/voters`, { signal }),
  comments: (postId: string, cursor: string | null, limit: number, signal?: AbortSignal) =>
    api.get<CursorPage<FeedComment>>(`/feed/posts/${enc(postId)}/comments`, {
      query: { cursor, limit },
      signal,
    }),
  createComment: (postId: string, body: { bodyHtml: string; attachmentIds: string[] }) =>
    api.post<FeedComment>(`/feed/posts/${enc(postId)}/comments`, body),
  updateComment: (id: string, body: { bodyHtml: string; attachmentIds: string[] }) =>
    api.put<FeedComment>(`/feed/comments/${enc(id)}`, body),
  removeComment: (id: string) => api.delete(`/feed/comments/${enc(id)}`),
  reactToComment: (id: string, type: ReactionType | null) =>
    type
      ? api.put<{ reactions: ReactionSummary[] }>(`/feed/comments/${enc(id)}/reaction`, { type })
      : api.delete<{ reactions: ReactionSummary[] }>(`/feed/comments/${enc(id)}/reaction`),
  commentReactions: (id: string, signal?: AbortSignal) =>
    api.get<ReactionUser[]>(`/feed/comments/${enc(id)}/reactions`, { signal }),
  counters: (signal?: AbortSignal) => api.get<FeedCounters>('/feed/counters', { signal }),
  seen: () => api.post('/feed/seen'),
  importantUnread: (signal?: AbortSignal) => api.get<ImportantItem[]>('/feed/important/unread', { signal }),
  groupAccess: (groupId: string, signal?: AbortSignal) =>
    api.get<GroupFeedAccess>(`/feed/groups/${enc(groupId)}/access`, { signal }),
  birthdays: (month: number, signal?: AbortSignal) =>
    api.get<BirthdayItem[]>('/users/birthdays', { query: { month }, signal }),
  newEmployees: (signal?: AbortSignal) =>
    api.get<NewEmployeeItem[]>('/users/new', { query: { days: 30 }, signal }),
};

export type StreamData = InfiniteData<CursorPage<Post>, string | null>;

/* ---------- Cache helpers ---------- */

/** Finds a post in any stream page, pinned list or single-post cache. */
export function findCachedPost(queryClient: QueryClient, id: string): Post | undefined {
  const single = queryClient.getQueryData<Post>(feedKeys.post(id));
  if (single) return single;
  for (const [, data] of queryClient.getQueriesData<StreamData>({ queryKey: feedKeys.streams })) {
    const found = data?.pages.flatMap((p) => p.items).find((p) => p.id === id);
    if (found) return found;
  }
  for (const [, data] of queryClient.getQueriesData<Post[]>({ queryKey: feedKeys.pinnedAll })) {
    const found = data?.find((p) => p.id === id);
    if (found) return found;
  }
  return undefined;
}
